import { useState } from 'react';
import { t } from '@bms2/shared';
import { AlertBanner } from '../../components/controls';
import { Modal } from '../../components/modals/Modal';
import modalStyles from '../../components/modals/modal.module.css';
import { PAGES_PATH, post } from './api';

/** 🔒 The route behind Fold into on the Sites tile (decision 1920). */
export const siteFoldPath = (siteId: string) => `${PAGES_PATH}/sites/${siteId}/fold`;

/** The row whose Fold into was pressed, and the row it folds into, as the tile carries them. */
export interface SiteFold {
  fromId: string;
  fromLabel: string;
  intoId: string;
  intoLabel: string;
}

/**
 * 🔒 Fold into (decision 1920): the window a site row's Fold into opens on
 * the Company's Sites tile, for a holder of the merge's key. It names both
 * rows and says what folding does before anything is written.
 *
 * 🔨 A refusal is the server's own sentence, such as a third row the folded
 * span would overlap, and the window stays open on it with nothing changed.
 */
export function FoldSiteModal({ fold, apiBaseUrl = '', onCancel, onFolded }: {
  fold: SiteFold;
  apiBaseUrl?: string;
  onCancel: () => void;
  onFolded: (sentence: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  const confirm = async () => {
    setBusy(true);
    setRefusal(null);
    const answer = await post<{ id: string; sentence: string }>(siteFoldPath(fold.fromId), { intoSiteId: fold.intoId }, apiBaseUrl);
    setBusy(false);
    if (!answer.ok) return setRefusal(answer.message);
    onFolded(answer.data?.sentence ?? t('sites.fold.done'));
  };

  return (
    <Modal
      open
      title={t('sites.fold.title')}
      subtitle={`${fold.fromLabel} → ${fold.intoLabel}`}
      dataModal="fold-site"
      onCancel={onCancel}
      actions={[
        { kind: 'primary', label: t('sites.fold.confirm'), onAct: () => void confirm(), disabled: busy },
        { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel },
      ]}
    >
      {refusal ? <AlertBanner tone="error" message={refusal} action={{ label: t('modal.dismiss'), onAction: () => setRefusal(null) }} /> : null}
      <p className={modalStyles.fieldLabel} data-fold-site-hint="true">{t('sites.fold.hint')}</p>
    </Modal>
  );
}
